/**
 * useErrorReporter — React hook that turns a thrown error from a mutate call
 * into:
 *
 *   1. A `ClassifiedError` (caller surfaces the `.copy` field inline), AND
 *   2. A best-effort observability event for `system`-classified errors only.
 *
 * Captures device attributes once on mount (`useMemo` with empty deps) so they
 * don't drift across reports — the user's viewport could change between mount
 * and an error event, but the report should reflect the original mount-time
 * device profile so the operator can correlate sessions cleanly.
 *
 * Plan ref:
 *   .plan/features/2026-05-04-error-handling-and-observability.md §React integration.
 *
 * Usage (RecipeCreationModal pattern):
 *   const report = useErrorReporter();
 *   try { await onCreate({...}); }
 *   catch (err) {
 *     const classified = report("recipe_create_failed", err,
 *       { entity: "recipe", mode },
 *       { mode, sourceRecipeUuid });
 *     setError(classified.copy);
 *   }
 *
 * Voice / privacy invariants:
 *   - Caller-supplied `context` is for IDs only — typed at the helper site
 *     to discourage misuse (no recipe titles, names, emails, etc).
 *   - The classifier returns brand-voice copy; the hook does not modify it.
 */

import { useCallback, useMemo } from "react";
import {
  classifyMutateError,
  reportClientError,
  type ClassifiedError,
  type ClassifyContext,
  type DeviceAttrs,
  type ReportContext,
} from "@proof/client-core/errors";

// ── Device-attr capture (browser side; safe-by-default in SSR) ────────────────

/**
 * Read device attrs from the DOM. Pure-ish — calls `navigator` / `window`
 * which are stable per-tab. Falls back to defensible defaults when the page
 * is server-rendered or running in a worker.
 */
function captureDeviceAttrs(): DeviceAttrs {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return {
      userAgent: "",
      viewportPx: { w: 0, h: 0 },
      locale: "",
      online: true,
      pwa: false,
      timezoneOffset: 0,
    };
  }
  const userAgent = typeof navigator.userAgent === "string" ? navigator.userAgent : "";
  // Some embedded browsers omit innerWidth/innerHeight; guard those too.
  const w = typeof window.innerWidth === "number" ? window.innerWidth : 0;
  const h = typeof window.innerHeight === "number" ? window.innerHeight : 0;
  const locale = typeof navigator.language === "string" ? navigator.language : "";
  const online = typeof navigator.onLine === "boolean" ? navigator.onLine : true;
  const pwa =
    typeof window.matchMedia === "function"
      ? window.matchMedia("(display-mode: standalone)").matches
      : false;
  let timezoneOffset = 0;
  try {
    timezoneOffset = new Date().getTimezoneOffset();
    // eslint-disable-next-line no-restricted-syntax -- best-effort: enriches a diagnostic payload with the tz offset; a throwing Date leaves the 0 default (Rule 8 documented degradation).
  } catch {
    // Some embedded JS runtimes throw on Date construction; defensive fallback.
  }
  return { userAgent, viewportPx: { w, h }, locale, online, pwa, timezoneOffset };
}

// ── Hook signature ────────────────────────────────────────────────────────────

/**
 * The returned `report` callback's signature:
 *
 *   (eventName, err, classifyCtx, reportContext?) => ClassifiedError
 *
 *   - `eventName`     — stable identifier the operator greps for (e.g.
 *                        "recipe_create_failed").
 *   - `err`           — the thrown value from a mutate call.
 *   - `classifyCtx`   — entity + optional mode for copy selection.
 *   - `reportContext` — IDs only (uuid, mode key, status code). Defaults to {}.
 *
 *   The function ALWAYS returns a `ClassifiedError` so the caller can render
 *   `.copy` inline. The reporter side-effect runs only when the classified
 *   kind is `system`.
 */
export type ReportFn = (
  eventName: string,
  err: unknown,
  classifyCtx: ClassifyContext,
  reportContext?: ReportContext,
) => ClassifiedError;

export interface UseErrorReporterOptions {
  /** Override the API base URL — defaults to "" (same-origin via the router). */
  baseUrl?: string;
  /** Override fetch — used by tests. */
  fetch?: typeof fetch;
  /** Override device attrs — used by tests. */
  device?: DeviceAttrs;
}

/**
 * Returns a stable callback that classifies + (for system errors) reports an
 * error from a mutate handler.
 *
 * Stable across renders. Device attrs captured once on mount.
 */
export function useErrorReporter(options: UseErrorReporterOptions = {}): ReportFn {
  const device = useMemo(() => options.device ?? captureDeviceAttrs(), [options.device]);
  const baseUrl = options.baseUrl ?? "";
  const fetchOverride = options.fetch;

  return useCallback<ReportFn>(
    (eventName, err, classifyCtx, reportContext) => {
      const classified = classifyMutateError(err, classifyCtx);
      if (classified.kind === "system") {
        // Best-effort: the reporter swallows its own failures.
        // We deliberately do NOT await — the caller is the React event
        // handler and should not block on a logging round-trip.
        const reportOpts: Parameters<typeof reportClientError>[4] = { device };
        if (baseUrl) reportOpts.baseUrl = baseUrl;
        if (fetchOverride) reportOpts.fetch = fetchOverride;
        void reportClientError(eventName, classified, err, reportContext ?? {}, reportOpts);
      }
      return classified;
    },
    [device, baseUrl, fetchOverride],
  );
}
